import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { getBackendBoolean, nullIfBackendEmptyId } from '@Sage/ssm-utils/backendScalars';
import { isEqual, toRelationUomIdString } from '@Sage/ssm-utils';
import { Modal, Text } from '@Sage/hoi-poi-ui';
import { MultiSelectReadCell, SectionListCrud } from '@Sage/ssm-web5';
import classnames from 'classnames';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    ConfigActions,
    EntityCrudActions,
    EntityExtraFieldsActions,
    EntityListActions,
    FuzzyActions,
    ServerListActions,
} from 'actions';
import CompaniesRow from 'components/selectRows/CompaniesRow';
import ProductLineDateCell from './ProductLineDateCell';
import { SALESORDERSLINES } from 'constants/Entities';
import { defineBaseExtraField, groupFieldsForFieldSelector } from 'utils/fm';
import { applySalesOrderLinesSmartFields } from 'utils/fm/fields';
import { getLiteral } from 'utils/getLiteral';
import { isSageImplementation as resolveIsSageImplementation } from 'utils/sage';
import { logEvent } from 'utils/tracking';

import {
    getProductLinesEnvironmentFilterValue,
    parseProductLinesEnvironmentValues,
    resolveProductLinesEnvironment,
} from './environment';
import ProductLinesEmptyView from '../../../components/ProductLinesEmptyView';
import CONFIG from '../../../components/SalesOrdersProductLinesTable/TableConfig';
import { getDefaultColumnsWidth } from '../../../utils';
import UnitOfMeasureCell, { SalesOrderLineUomConfirmContext } from './UnitOfMeasureCell';

/** Order lines: valores calculados en OrdersManager; deben mostrarse como texto (currencyCell), no como InputCell readonly (SectionListCrud transforma si el colId está en crudSchema). */
const CALCULATED_CURRENCY_COLUMN_IDS = ['subtotalAmount', 'lineDiscountTotalAmount', 'finalprice'];

const initialState = { groupedFields: null, columnsSizes: null, columnsVisibility: null };

const reducer = (state, action) => {
    switch (action.type) {
        case 'setColumnsSizes':
            return { ...state, columnsSizes: action.columnsSizes };
        case 'setColumnsVisibility':
            return { ...state, columnsVisibility: action.columnsVisibility };
        case 'setState':
            return { ...action.state };
    }
};

const ProductLinesTable = memo(
    ({
        cacheId,
        changeField,
        crudSchema,
        data,
        disableEditSalesOrdersLines,
        errors,
        handleOnRef,
        handleSetCheckedProducts,
        idCurrency,
        idEnvironment,
        isNewEntity,
        parentConfigMap,
        parentData,
        copilotModifiedProductLines,
    }) => {
        // --- Redux
        const dispatch = useDispatch();

        const {
            cache,
            canDeleteOrderLines,
            isSageImplementation,
            salesOrderAmountDiscounts,
            syncStatusEnabled,
            uomEnabled,
            useTaxesSalesOrders,
        } = useSelector((state) => {
            const oldCache = state?.config?.components?.[`${SALESORDERSLINES.entity}_list`] || null;
            const newCache = (cacheId && state?.config?.components?.[cacheId]) || null;
            const permission = state.config.permission;

            return {
                cache: newCache || oldCache,
                canDeleteOrderLines: permission.crud_permission[SALESORDERSLINES.permission].delete,
                isSageImplementation: resolveIsSageImplementation(state.config?.productModeCode),
                salesOrderAmountDiscounts: !!state?.config?.userData?.salesOrderAmountDiscounts,
                syncStatusEnabled: getBackendBoolean(
                    state?.config?.userData?.syncStatusEnabled || false,
                ),
                uomEnabled: !!state?.config?.userData?.uomEnabled,
                useTaxesSalesOrders: getBackendBoolean(
                    state?.config?.userData?.useTaxesSalesOrders || false,
                ),
            };
        }, shallowEqual);

        const {
            getExtraFieldSchema,
            getFuzzy,
            getList,
            setConfigWeb,
            standardFieldsConfiguration,
            updateTableErrors,
        } = useMemo(
            () => ({
                getExtraFieldSchema: bindActionCreators(EntityExtraFieldsActions, dispatch)
                    .getExtraFieldSchema,
                getFuzzy: bindActionCreators(FuzzyActions, dispatch).getFuzzy,
                getList: bindActionCreators(ServerListActions, dispatch).getList,
                setConfigWeb: bindActionCreators(ConfigActions, dispatch).setConfigWeb,
                standardFieldsConfiguration: bindActionCreators(EntityListActions, dispatch)
                    .standardFieldsConfiguration,
                updateTableErrors: bindActionCreators(EntityCrudActions, dispatch)
                    .updateTableErrors,
            }),
            [dispatch],
        );

        // --- State
        const [refreshColumnDefs, setRefreshColumnDefs] = useState(false);
        const [state, dispatchLocal] = useReducer(reducer, initialState);
        const [showModalFields, setShowModalFields] = useState(false);
        // Pending UoM change awaiting user confirmation (Task E / Phase 4).
        const [pendingUomChange, setPendingUomChange] = useState(null);
        // Bumped when a UoM change needs confirmation so the Select remounts and shows the saved unit (not react-select's internal pick).
        const [uomSelectLayoutKey, setUomSelectLayoutKey] = useState(0);
        const errorsRef = useRef(errors);
        const mappedFields = useRef(null);
        const parentDataRef = useRef(parentData);
        const copilotModifiedProductLinesRef = useRef(copilotModifiedProductLines);
        copilotModifiedProductLinesRef.current = copilotModifiedProductLines;
        const columnsConfigRef = useRef({ sizes: {}, visibles: {} });
        const pendingCacheRef = useRef({ sizes: {}, visibles: {} });
        const pendingColumnSizesRef = useRef({});
        const saveCacheTimerRef = useRef(null);
        columnsConfigRef.current = {
            sizes: {
                ...(cache?.sizes || {}),
                ...(state.columnsSizes || {}),
                ...pendingColumnSizesRef.current,
            },
            visibles: state.columnsVisibility || cache?.visibles || {},
        };

        useEffect(() => {
            pendingColumnSizesRef.current = {};
        }, [cache?.sizes]);
        const effectiveIdEnvironment = useMemo(() => {
            return resolveProductLinesEnvironment({ idEnvironment, parentData });
        }, [idEnvironment, parentData]);
        const environmentFilterValue = useMemo(() => {
            return getProductLinesEnvironmentFilterValue({ idEnvironment, parentData });
        }, [idEnvironment, parentData]);

        // --- Derived state
        const { finalData, finalCrudSchema, schemaHiddenMap } = useMemo(() => {
            const { schema: finalCrudSchema, rows } = applySalesOrderLinesSmartFields({
                crudSchema,
                rows: data,
                parentConfigMap,
                parentData,
                isNewEntity,
            });
            const updatedData = rows?.length ? rows : null;

            // Create a map to quickly check if a field is hidden by fieldConfiguration or id
            const schemaHiddenMap = finalCrudSchema.reduce((map, field) => {
                const fieldKey = field.fieldConfiguration || field.id;
                const isHidden = field.hidden === true;
                if (fieldKey) {
                    map[fieldKey] = isHidden;
                    // Also map by id for extra fields
                    if (field.isExtra && field.id) {
                        map[field.id.toLowerCase()] = isHidden;
                    }
                }
                return map;
            }, {});

            return { finalData: updatedData, finalCrudSchema, schemaHiddenMap };
        }, [data, parentData, parentConfigMap, isNewEntity, crudSchema]);

        const multiSelectFieldsByColId = useMemo(() => {
            const map = new Map();
            (finalCrudSchema || []).forEach((field) => {
                if (
                    field.type === 'multipleValueList' ||
                    field.type === 'fuzzySearchMultiple' ||
                    field.type === 'multiSelect'
                ) {
                    map.set(field.id, field);
                }
            });
            return map;
        }, [finalCrudSchema]);

        // --- Effects
        useEffect(() => {
            errorsRef.current = errors;
        }, [errors]);

        useEffect(() => {
            if (!isEqual(parentDataRef.current, parentData)) {
                setRefreshColumnDefs(true);
                parentDataRef.current = parentData;
            }
        }, [parentData]);

        useEffect(() => {
            if (!refreshColumnDefs) return;
            setRefreshColumnDefs(false);
        }, [refreshColumnDefs]);

        useEffect(() => () => clearTimeout(saveCacheTimerRef.current), []);

        // --- Handlers & column configuration
        const tableParams = useMemo(() => {
            let newTableParams = { numLine: { isCrud: true } };

            if (!canDeleteOrderLines) return newTableParams;

            newTableParams.numLine.onClickCheckbox = (result) => {
                const { checked } = result;
                const checkedRows = checked.numLine.rows;
                const newCheckedRows = Object.keys(checkedRows).filter((key) => {
                    return checkedRows[key];
                });
                handleSetCheckedProducts(newCheckedRows);
            };

            return newTableParams;
        }, [canDeleteOrderLines, handleSetCheckedProducts]);

        const saveCache = useCallback(
            (newObject) => {
                pendingCacheRef.current = {
                    sizes: newObject.sizes || {},
                    visibles: newObject.visibles || {},
                };
                clearTimeout(saveCacheTimerRef.current);
                saveCacheTimerRef.current = setTimeout(() => {
                    setConfigWeb(`${SALESORDERSLINES.entity}_list`, pendingCacheRef.current);
                }, 300);
            },
            [setConfigWeb],
        );

        const getCacheConfiguration = useCallback(
            (columnsDef) => {
                const sizes = columnsConfigRef.current.sizes || {};
                const visibles = columnsConfigRef.current.visibles || {};
                const mapped = columnsDef.map((column) => {
                    // Mandatory fields should never be hidden - check first to avoid unnecessary operations
                    const isMandatory = column?.cellRendererParams?.inputParams?.isMandatory;
                    const col = { ...column };
                    if (col.alwaysHidden) {
                        col.hide = true;
                    } else if (isMandatory) {
                        col.hide = false;
                    } else {
                        // Check if column should be hidden based on schema (smart fields)
                        const columnField = col.field || col.colId;
                        const isHiddenBySchema =
                            columnField &&
                            (schemaHiddenMap[columnField] ||
                                schemaHiddenMap[columnField.toLowerCase()]);

                        // Priority: schema hidden > cache visibles
                        if (isHiddenBySchema) {
                            col.hide = true;
                        } else if (visibles.hasOwnProperty(col.colId)) {
                            col.hide = !visibles[col.colId];
                        }
                    }

                    if (sizes?.[col.colId]) col.width = sizes[col.colId];

                    return col;
                });

                if (!salesOrderAmountDiscounts) return mapped;

                return mapped.map((column) => {
                    const tierMatch = /^discount([1-4])Value$/.exec(column.colId);
                    if (!tierMatch) return column;
                    if (column.alwaysHidden) return column;
                    const pctId = `discount${tierMatch[1]}`;
                    if (visibles[pctId] !== undefined && visibles[column.colId] === undefined) {
                        return { ...column, hide: !visibles[pctId] };
                    }
                    return column;
                });
            },
            [schemaHiddenMap, salesOrderAmountDiscounts],
        );

        const entityValueListName = useMemo(
            () => ({
                Productos: 'products',
                coferta: 'offers',
                empresas: 'companies',
                Expedientes: 'opportunities',
                viewUsuariosFullName: 'users',
                viewContactosFullName: 'contacts',
            }),
            [],
        );

        const selectCompanyRow = useCallback((props) => {
            if (!props.data) return null;

            return (
                <CompaniesRow
                    info2={props.data.ExtraInfo}
                    info3={props.data.ExtraInfo3}
                    text={props.data.MatchingInfo}
                    {...props}
                />
            );
        }, []);

        const selectComponents = useCallback(
            ({ list }) => {
                const finalEntity = entityValueListName[list] || list;
                const components = { companies: selectCompanyRow };

                return { Option: components[finalEntity] || null };
            },
            [selectCompanyRow, entityValueListName],
        );

        const fieldSelectorLinkedColumnIds = useMemo(() => {
            if (!salesOrderAmountDiscounts) return undefined;
            const tiers = isSageImplementation ? [1] : [1, 2, 3, 4];
            return tiers.reduce((acc, n) => {
                acc[`discount${n}`] = [`discount${n}Value`];
                return acc;
            }, {});
        }, [salesOrderAmountDiscounts, isSageImplementation]);

        const saveColumnSize = useCallback(
            (colId, size) => {
                const newSizes = {
                    ...columnsConfigRef.current.sizes,
                    ...pendingColumnSizesRef.current,
                    [colId]: size,
                };
                pendingColumnSizesRef.current = newSizes;
                columnsConfigRef.current = {
                    ...columnsConfigRef.current,
                    sizes: newSizes,
                };
                saveCache({
                    sizes: newSizes,
                    visibles: columnsConfigRef.current.visibles,
                });
            },
            [saveCache],
        );

        const saveVisibleColumns = useCallback(
            (newColumns) => {
                const newColumnsVisibility = {
                    ...columnsConfigRef.current.visibles,
                    ...newColumns,
                };
                columnsConfigRef.current = {
                    ...columnsConfigRef.current,
                    visibles: newColumnsVisibility,
                };

                saveCache({
                    sizes: columnsConfigRef.current.sizes,
                    visibles: newColumnsVisibility,
                });
                dispatchLocal({
                    type: 'setColumnsVisibility',
                    columnsVisibility: newColumnsVisibility,
                });
            },
            [saveCache],
        );

        const loadOptionsFuzzy = useCallback(
            (inputAttrs = {}) => {
                return (text = '') => {
                    const searchText = text ?? '';

                    return getFuzzy({
                        list: inputAttrs.list,
                        field: inputAttrs.field,
                        text: searchText,
                        feature: inputAttrs.feature,
                        parentField: inputAttrs.parentFieldForBackend || inputAttrs.parentField,
                        parentValue: inputAttrs.parentValue,
                        idParent: inputAttrs.idParent,
                        environmentId: effectiveIdEnvironment,
                        excludeDisabledStates: inputAttrs.excludeDisabledStates,
                    }).then((options) => {
                        return options.map((current) => ({
                            ...current,
                            value: current.value ?? current.Id,
                            label: current.label ?? current.MatchingInfo,
                        }));
                    });
                };
            },
            [effectiveIdEnvironment, getFuzzy],
        );

        const loadOptions = useCallback(
            ({ list }) => {
                return () => {
                    return getList(list).then((options) => {
                        const finalOptions = options || [];

                        if (!environmentFilterValue) return finalOptions;

                        return finalOptions.filter((option) => {
                            const envValues = parseProductLinesEnvironmentValues(
                                option?.stridenvironment,
                            );

                            return (
                                !option?.stridenvironment ||
                                envValues.length === 0 ||
                                envValues.includes('-1') ||
                                envValues.includes(environmentFilterValue)
                            );
                        });
                    });
                };
            },
            [environmentFilterValue, getList],
        );

        const getMappedFields = useCallback((columnDefs) => {
            mappedFields.current = columnDefs.reduce((obj, current) => {
                obj[current.field] = current;
                return obj;
            }, {});
        }, []);

        /** Align grid row with `finalData` index (OrdersManager uses array index). */
        const resolveProductLineRow = useCallback(
            (rowIndex, rowId) => {
                if (!finalData?.length) {
                    return { row: null, index: -1 };
                }
                if (rowId !== null && rowId !== undefined && rowId !== '') {
                    const idx = finalData.findIndex(
                        (r) => String(r.id ?? r.Id ?? r.ID) === String(rowId),
                    );
                    if (idx >= 0) {
                        return { row: finalData[idx], index: idx };
                    }
                }
                if (typeof rowIndex === 'number' && finalData[rowIndex]) {
                    return { row: finalData[rowIndex], index: rowIndex };
                }
                return { row: null, index: -1 };
            },
            [finalData],
        );

        const getStablePendingUomRowKey = useCallback((rowIndex, rowId) => {
            const stableId = rowId ?? '';
            return stableId !== '' ? String(stableId) : `row-index:${rowIndex ?? ''}`;
        }, []);

        const isLineCellEnriched = useCallback((row, field) => {
            const cells = copilotModifiedProductLinesRef.current?.[row?.id];
            if (!cells || !field) return false;
            return Boolean(cells[field] || cells[String(field).toLowerCase()]);
        }, []);

        const onChangeCell = useCallback(
            ({ rowIndex, id, field, value }) => {
                // Calculated fields are derived in OrdersManager and must not be edited directly.
                const calculatedFields = [
                    'finalprice',
                    'finalPrice',
                    'subtotalAmount',
                    'lineDiscountTotalAmount',
                    'totalAmount',
                ];
                if (calculatedFields.includes(field)) return;
                if (field === 'taxRateId' && syncStatusEnabled) return;

                let rowIndexForManager = rowIndex;
                // Do not apply UoM until the user confirms the modal; remount Select so it
                // keeps showing the saved unit instead of react-select's temporary pick.
                if (field === 'idRelationUoM') {
                    const { row, index } = resolveProductLineRow(rowIndex, id);
                    if (index >= 0) {
                        rowIndexForManager = index;
                    }
                    const currentUomId =
                        nullIfBackendEmptyId(toRelationUomIdString(row?.idRelationUoM) || null) ||
                        '';
                    const nextUomId =
                        nullIfBackendEmptyId(toRelationUomIdString(value) || null) || '';

                    if (currentUomId === nextUomId) {
                        return;
                    }

                    if (row) {
                        setPendingUomChange({ rowIndex: rowIndexForManager, id, field, value });
                        setUomSelectLayoutKey((k) => k + 1);
                        return;
                    }
                }

                changeField('product')({ rowIndex: rowIndexForManager, id, field, value });
                if (!mappedFields.current) return;
                if (errorsRef?.current && Object.keys(errorsRef.current).length > 0) {
                    const newErrors = { ...errorsRef.current };
                    const colId = mappedFields.current?.[field]?.colId;
                    const rowError = errorsRef.current?.[colId]?.rows?.[rowIndex]?.error;
                    const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
                    if (colId && (rowError === 'mandatory' || rowError === true) && hasValue) {
                        delete newErrors[colId].rows[rowIndex];
                        updateTableErrors(newErrors);
                    }
                }
            },
            [changeField, resolveProductLineRow, syncStatusEnabled, updateTableErrors],
        );

        const confirmUomChange = useCallback(() => {
            if (!pendingUomChange) return;
            changeField('product')(pendingUomChange);
            setPendingUomChange(null);
        }, [changeField, pendingUomChange]);

        const cancelUomChange = useCallback(() => {
            setPendingUomChange(null);
        }, []);

        const getColumnDefs = useCallback(
            ({ transformTableToCrud }) => {
                return new Promise((resolve, reject) => {
                    const newTableParams = {
                        ...tableParams,
                        data: finalData,
                        disabledCheckboxes: disableEditSalesOrdersLines,
                        salesOrderData: parentDataRef.current,
                        useTaxesSalesOrders,
                        isSageImplementation,
                        salesOrderAmountDiscounts,
                        uomEnabled,
                    };
                    let newConfig = CONFIG(newTableParams);
                    // Presentation for calculated amounts: always use TableConfig's currencyCell
                    // params. After standardFields / transformTableToCrud the column may no longer
                    // report cellRenderer === 'currencyCell', which skipped our previous restore.
                    const currencyPresentationParamsByColId = new Map(
                        newConfig.columnDefs
                            .filter(
                                (col) =>
                                    CALCULATED_CURRENCY_COLUMN_IDS.includes(col.colId) &&
                                    col.cellRenderer === 'currencyCell',
                            )
                            .map((col) => [col.colId, col.cellRendererParams]),
                    );

                    getExtraFieldSchema(newConfig.entity, (extraFieldsTabs) => {
                        let extraFields = [];
                        const groupedExtraFields = extraFieldsTabs.map((tab) => {
                            let group = {
                                label:
                                    typeof tab.descripcion !== 'string'
                                        ? 'label_customized_fields'
                                        : tab.descripcion,
                                fields: [],
                            };

                            group.fields = tab.tabFields.map((extraField) => {
                                const extraParams = {
                                    decimalAndIntegerParams: { emptyIfNull: true },
                                    entityName: newConfig.entity.extraFieldName,
                                    contextData: {
                                        ...parentDataRef.current,
                                    },
                                };
                                let newExtraField = defineBaseExtraField(extraField, extraParams);
                                if (newExtraField.cellRenderer === 'dateCell') {
                                    newExtraField.cellRendererParams.CustomCell =
                                        ProductLineDateCell;
                                }

                                extraFields.push(newExtraField);
                                return newExtraField;
                            });

                            return group;
                        });

                        standardFieldsConfiguration(SALESORDERSLINES, newConfig.columnDefs)
                            .then((columnDefs) => {
                                let newColumnDefs = [...columnDefs, ...extraFields];

                                const groupedFields = [
                                    ...groupFieldsForFieldSelector(newConfig, columnDefs, true),
                                    ...groupedExtraFields,
                                ];

                                if (finalCrudSchema) {
                                    newColumnDefs = transformTableToCrud({
                                        columnDefs: newColumnDefs,
                                        crudSchema: finalCrudSchema,
                                        inputProps: {
                                            onChange: onChangeCell,
                                            loadOptions,
                                            loadOptionsFuzzy,
                                            selectComponents,
                                            isEnriched: isLineCellEnriched,
                                        },
                                    });

                                    if (salesOrderAmountDiscounts) {
                                        const percentInputParamsByTier = new Map();
                                        newColumnDefs.forEach((column) => {
                                            const percentMatch = /^discount([1-4])$/.exec(
                                                column.colId || '',
                                            );
                                            if (!percentMatch) return;
                                            percentInputParamsByTier.set(
                                                percentMatch[1],
                                                column.cellRendererParams?.inputParams || {},
                                            );
                                        });

                                        newColumnDefs = newColumnDefs.map((column) => {
                                            const valueMatch = /^discount([1-4])Value$/.exec(
                                                column.colId || '',
                                            );
                                            if (!valueMatch) return column;

                                            const sourceInputParams = percentInputParamsByTier.get(
                                                valueMatch[1],
                                            );
                                            if (!sourceInputParams) return column;

                                            const inheritedInputParams = {};
                                            [
                                                'isReadOnly',
                                                'readOnly',
                                                'isMandatory',
                                                'mandatory',
                                                'required',
                                            ].forEach((key) => {
                                                if (sourceInputParams[key] !== undefined) {
                                                    inheritedInputParams[key] =
                                                        sourceInputParams[key];
                                                }
                                            });

                                            if (!Object.keys(inheritedInputParams).length) {
                                                return column;
                                            }

                                            return {
                                                ...column,
                                                cellRendererParams: {
                                                    ...column.cellRendererParams,
                                                    inputParams: {
                                                        ...column.cellRendererParams?.inputParams,
                                                        ...inheritedInputParams,
                                                    },
                                                },
                                            };
                                        });
                                    }

                                    // Tax select in order lines must never be clearable.
                                    // Tax rate is part of the line calculation context and
                                    // should always have a concrete value.
                                    // SectionListCrud overwrites inputParams.isReadOnly from schema only;
                                    // re-apply here so syncStatusEnabled can lock the field.
                                    newColumnDefs = newColumnDefs.map((column) => {
                                        if (column.colId !== 'taxRateId') return column;

                                        const schemaReadOnly =
                                            !!column.cellRendererParams?.inputParams?.isReadOnly;
                                        const taxRateReadOnly =
                                            syncStatusEnabled ||
                                            schemaReadOnly ||
                                            disableEditSalesOrdersLines;

                                        if (taxRateReadOnly) {
                                            return {
                                                ...column,
                                                cellRenderer: 'textCell',
                                                crudType: undefined,
                                                valueGetter: (params) => {
                                                    const d = params.data;
                                                    if (!d) return '';
                                                    if (d.taxRate != null && d.taxRate !== '') {
                                                        return String(d.taxRate);
                                                    }
                                                    const tr = d.taxRateId;
                                                    if (tr && typeof tr === 'object' && tr.label) {
                                                        return String(tr.label);
                                                    }
                                                    return tr != null && tr !== ''
                                                        ? String(tr)
                                                        : '';
                                                },
                                                cellRendererParams: {
                                                    align: 'left',
                                                },
                                            };
                                        }

                                        return {
                                            ...column,
                                            cellRendererParams: {
                                                ...column.cellRendererParams,
                                                inputParams: {
                                                    ...column.cellRendererParams?.inputParams,
                                                    isClearable: false,
                                                    isReadOnly: false,
                                                    readOnly: false,
                                                },
                                            },
                                        };
                                    });

                                    newColumnDefs = newColumnDefs.map((column) => {
                                        let col = column;

                                        if (uomEnabled && column.colId === 'idRelationUoM') {
                                            col = {
                                                ...col,
                                                cellRenderer: UnitOfMeasureCell,
                                                crudType: undefined,
                                                cellRendererParams: {
                                                    ...col.cellRendererParams,
                                                    isSageImplementation,
                                                    onChange: onChangeCell,
                                                    isEnriched: isLineCellEnriched,
                                                },
                                            };
                                        }

                                        if (CALCULATED_CURRENCY_COLUMN_IDS.includes(col.colId)) {
                                            const presentationParams =
                                                currencyPresentationParamsByColId.get(col.colId);
                                            if (presentationParams) {
                                                col = {
                                                    ...col,
                                                    cellRenderer: 'currencyCell',
                                                    cellRendererParams: presentationParams,
                                                };
                                            }
                                        }

                                        const multiSelectField = multiSelectFieldsByColId.get(
                                            col.colId,
                                        );
                                        if (disableEditSalesOrdersLines && multiSelectField) {
                                            col = {
                                                ...col,
                                                cellRenderer: MultiSelectReadCell,
                                                crudType: undefined,
                                                cellRendererParams: {
                                                    ...col.cellRendererParams,
                                                    fieldLabel:
                                                        multiSelectField.label || col.headerName,
                                                },
                                            };
                                        }

                                        return col;
                                    });
                                }

                                newColumnDefs = getDefaultColumnsWidth(newColumnDefs);

                                newColumnDefs = getCacheConfiguration(newColumnDefs);

                                dispatchLocal({
                                    type: 'setState',
                                    state: {
                                        groupedFields: groupedFields,
                                        columnsSizes: cache?.sizes || [],
                                        columnsVisibility: cache?.visibles || [],
                                    },
                                });
                                getMappedFields(newColumnDefs);
                                resolve(newColumnDefs);
                            })
                            .catch((error) => {
                                console.error('List configuration error:', error);
                                reject(error);
                            });
                    });
                });
            },
            [
                tableParams,
                getExtraFieldSchema,
                standardFieldsConfiguration,
                getCacheConfiguration,
                finalCrudSchema,
                getMappedFields,
                onChangeCell,
                isLineCellEnriched,
                loadOptions,
                loadOptionsFuzzy,
                cache?.sizes,
                cache?.visibles,
                selectComponents,
                finalData,
                multiSelectFieldsByColId,
                disableEditSalesOrdersLines,
                useTaxesSalesOrders,
                syncStatusEnabled,
                isSageImplementation,
                salesOrderAmountDiscounts,
                uomEnabled,
            ],
        );

        const fieldSelectorProps = useMemo(() => {
            if (!state.groupedFields) return null;
            let actions = [];

            actions.push({ type: 'title', label: getLiteral('label_customize') });

            actions.push({
                label: getLiteral('action_select_visible_columns'),
                icon: 'columns',
                id: 'columns',
                onClick: () => {
                    setShowModalFields(true);
                    logEvent({
                        event: SALESORDERSLINES.trueName,
                        functionality: 'visibleFields',
                        checkDuplicate: true,
                    });
                },
            });

            return {
                title: getLiteral('label_showHideColumns'),
                actions,
                groupedColumns: state.groupedFields,
            };
        }, [state.groupedFields]);

        const modalOptionsProps = useCallback(
            () => ({
                advice: getLiteral('label_visible_fields_explanation'),
                isOpen: showModalFields,
                confirmText: getLiteral('action_save'),
                cancelText: getLiteral('action_cancel'),
                placeholderSearch: getLiteral('label_search_field'),
                title: getLiteral('label_visible_fields'),
                onCancel: setShowModalFields,
            }),
            [showModalFields],
        );

        const onOrderProducts = useCallback(
            (event) => {
                //Here we use a promise because the table expects a promise
                return new Promise((resolve, reject) => {
                    let newProducts = [];
                    event.api.forEachNode((current) => {
                        let newProduct = current.data;
                        newProduct.intOrder = current.rowIndex;
                        newProduct.numLine = current.rowIndex + 1;
                        newProducts.push(newProduct);
                    });

                    changeField('orderedProducts')(newProducts);
                    resolve();
                });
            },
            [changeField],
        );

        const columnsRefreshOnDragEnd = useMemo(() => ['numLine', 'rowNumber'], []);

        // --- Derived (render)
        const { domLayout, extraClassName } = useMemo(() => {
            // 50px each row, 400px of max-height for the table
            // we have only the header, so we have 350px for rows -> 7 rows
            if (!finalData?.length) {
                return {
                    domLayout: 'normal',
                    extraClassName: 'sales-order-product-lines-table__empty',
                };
            } else if (finalData?.length <= 7)
                return { domLayout: 'autoHeight', extraClassName: '' };
            else {
                return { domLayout: 'normal', extraClassName: 'table-with-many-rows' };
            }
        }, [finalData?.length]);

        const getRowNodeId = useCallback((data) => data?.id, []);

        // --- Memoized props
        const sectionListCrudProps = useMemo(
            () => ({
                className: classnames(['sales-order-product-lines-table', extraClassName]),
                columnsRefreshOnDragEnd,
                customEmptyViewComponent: ProductLinesEmptyView,
                disabledCheckboxes: disableEditSalesOrdersLines,
                domLayout,
                errors,
                fieldSelector: true,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                getColumnDefs,
                getRowNodeId,
                id: 'sales-order-product-lines-table',
                idCurrency,
                idEnvironment: effectiveIdEnvironment,
                // Line smart fields make mandatory row-dependent, so it is shown per cell.
                mandatoryIndicator: 'cell',
                modalOptionsProps: modalOptionsProps(),
                onDragEnd: onOrderProducts,
                onRef: handleOnRef,
                rowData: finalData,
                saveColumnSize,
                saveVisibleColumns,
                shouldRefreshColumnDefs: refreshColumnDefs,
                useDragRows: !disableEditSalesOrdersLines,
                useSort: false,
                // Avoid global refreshCells on each row edit; finalData already carries overrides.
                forceRowUpdating: false,
            }),
            [
                columnsRefreshOnDragEnd,
                disableEditSalesOrdersLines,
                domLayout,
                errors,
                extraClassName,
                fieldSelectorLinkedColumnIds,
                fieldSelectorProps,
                finalData,
                getColumnDefs,
                getRowNodeId,
                handleOnRef,
                idCurrency,
                effectiveIdEnvironment,
                modalOptionsProps,
                onOrderProducts,
                refreshColumnDefs,
                saveColumnSize,
                saveVisibleColumns,
            ],
        );

        const uomConfirmContextValue = useMemo(
            () => ({
                pendingRowId: pendingUomChange?.id ?? null,
                pendingRowKey: pendingUomChange
                    ? getStablePendingUomRowKey(
                          pendingUomChange.rowIndex,
                          pendingUomChange.id ?? '',
                      )
                    : null,
                layoutKey: uomSelectLayoutKey,
                onUomChange: onChangeCell,
            }),
            [pendingUomChange, uomSelectLayoutKey, onChangeCell, getStablePendingUomRowKey],
        );

        // --- Render
        return (
            <SalesOrderLineUomConfirmContext.Provider value={uomConfirmContextValue}>
                <SectionListCrud {...sectionListCrudProps} />
                {pendingUomChange && (
                    <Modal
                        isOpen={!!pendingUomChange}
                        title={getLiteral('title_confirm_unit_of_measure_change')}
                        onConfirm={confirmUomChange}
                        onCancel={cancelUomChange}
                        confirmText={getLiteral('action_confirm')}
                        cancelText={getLiteral('action_cancel')}
                    >
                        <Text>{getLiteral('confirm_unit_of_measure_change')}</Text>
                    </Modal>
                )}
            </SalesOrderLineUomConfirmContext.Provider>
        );
    },
);

ProductLinesTable.displayName = 'ProductLinesTable';

export default ProductLinesTable;
